import React, { useEffect, useState, useContext } from "react";
import { getClasses } from "../../api/classSubjectApi";
import { AuthContext } from "../../store/AuthContext";
import {
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  useTheme
} from "@mui/material";
import { useNavigate } from "react-router-dom";

export default function StudentSelectClassSSO() {
  const { token } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  // Load classes
  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setLoading(true);
      const res = await getClasses(token);
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading classes:", err);
      setError("Failed to load classes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          background: "#000",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <CircularProgress size={50} />
        <Typography variant="h6" color="#ccc" sx={{ mt: 2 }}>
          Loading Classes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4, background: "#000", minHeight: "100vh" }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadClasses}>
              RETRY
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ background: "#000", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              mb: 1,
              background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
              WebkitBackgroundClip: "text",
              color: "transparent"
            }}
          >
            Select Your Class
          </Typography>

          <Typography variant="body1" color="#bbb">
            Choose your class to explore available subjects.
          </Typography>
        </Box>

        {/* Class Grid */}
        <Grid container spacing={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          {classes.map((cls) => (
            <Grid item xs={12} sm={6} md={4} key={cls.id}>
              <Card
                onClick={() =>
                  navigate(`/student/select-subject-sso?class_id=${cls.id}`)
                }
                sx={{
                  cursor: "pointer",
                  height: 220,
                  width: 200,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  background: "#111",
                  border: "1px solid #222",
                  borderRadius: "20px",
                  transition: "0.2s",
                  "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: "0 0 12px rgba(255, 255, 255, 1)"
                  }
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      color: "#fff"
                    }}
                  >
                    Class {cls.class}
                  </Typography>

                  {cls.description && (
                    <Typography
                      variant="body2"
                      color="#aaa"
                      sx={{ mt: 1 }}
                    >
                      {cls.description}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      mt: 2,
                      width: 120,
                      height: 6,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderRadius: 2,
                      opacity: 0.9
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {classes.length === 0 && (
          <Box textAlign="center" py={6}>
            <Typography variant="h6" color="#ccc">
              No classes available
            </Typography>
            <Typography variant="body2" color="#777">
              Please check again later.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
